import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import useFocusTrap from '../../hooks/useFocusTrap';
import './LoginModal.css';

const LoginModal = ({ isOpen, onClose, onSuccess, actionMessage }) => {
  const { loginWithGoogle, loginWithEmail, signupWithEmail } = useAuth();
  const modalRef = useFocusTrap(isOpen);
  const [isSignup, setIsSignup] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setEmail('');
      setPassword('');
      setError('');
      setIsSignup(false);
      setAgeConfirmed(false);
      setTermsAccepted(false);
    }
  }, [isOpen]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setError('');

    if (isSignup && (!ageConfirmed || !termsAccepted)) {
      setError('Please confirm your age and accept the Terms of Service');
      return;
    }

    setLoading(true);

    try {
      let result;
      if (isSignup) {
        result = await signupWithEmail(email, password);
      } else {
        result = await loginWithEmail(email, password);
      }
      // Success! Call the callback with the user
      if (onSuccess) {
        onSuccess(result?.user || null);
      }
      onClose();
    } catch (error) {
      if (error.code === 'auth/user-not-found') {
        setError('No account found with this email.');
      } else if (error.code === 'auth/wrong-password') {
        setError('Incorrect password.');
      } else if (error.code === 'auth/email-already-in-use') {
        setError('An account with this email already exists.');
      } else if (error.code === 'auth/weak-password') {
        setError('Password should be at least 6 characters.');
      } else if (error.code === 'auth/invalid-email') {
        setError('Invalid email address.');
      } else {
        setError(error.message || 'Authentication failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setError('');

    if (isSignup && (!ageConfirmed || !termsAccepted)) {
      setError('Please confirm your age and accept the Terms of Service');
      return;
    }

    setLoading(true);
    try {
      const result = await loginWithGoogle();
      // Success! Call the callback with the user
      if (onSuccess) {
        onSuccess(result?.user || null);
      }
      onClose();
    } catch (error) {
      setError('Google sign-in failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !loading) {
      onClose();
    }
  };

  const handleEscapeKey = useCallback((e) => {
    if (e.key === 'Escape' && !loading) {
      onClose();
    }
  }, [loading, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
      return () => document.removeEventListener('keydown', handleEscapeKey);
    }
  }, [isOpen, handleEscapeKey]);

  if (!isOpen) return null;

  return (
    <div className="login-modal-backdrop" onClick={handleBackdropClick}>
      <div className="login-modal-container" role="dialog" aria-modal="true" aria-labelledby="login-modal-title" ref={modalRef}>
        {/* Close button */}
        <button
          className="login-modal-close"
          onClick={onClose}
          disabled={loading}
          aria-label="Close"
        >
          ✕
        </button>

        {/* Header */}
        <div className="login-modal-header">
          <div className="brand-medium">
            <span className="brand-badge-medium">CS</span>
            <div className="brand-text-medium">
              <span className="brand-title-medium">Camp Scout</span>
            </div>
          </div>
          <h3 className="login-modal-title" id="login-modal-title">
            {isSignup ? 'Create an account' : (actionMessage || 'Sign in to continue')}
          </h3>
          <p className="login-modal-subtitle">
            {isSignup ? 'Sign up to access all features' : 'Create an account or sign in to access all features'}
          </p>
        </div>

        {/* Login Form */}
        <form className="login-modal-form" onSubmit={handleEmailAuth}>
          <div className="form-field">
            <label className="field-label" htmlFor="modal-email">
              Email
            </label>
            <input
              id="modal-email"
              className="text-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
              disabled={loading}
              aria-describedby={error ? 'login-modal-error' : undefined}
            />
          </div>

          <div className="form-field">
            <label className="field-label" htmlFor="modal-password">
              Password
            </label>
            <input
              id="modal-password"
              className="text-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              disabled={loading}
              minLength={6}
            />
          </div>

          {error && <span id="login-modal-error" className="form-feedback-error" role="alert">{error}</span>}

          {isSignup && (
            <div className="auth-checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={ageConfirmed}
                  onChange={(e) => setAgeConfirmed(e.target.checked)}
                />
                I am 13 years of age or older
              </label>
            </div>
          )}

          {isSignup && (
            <div className="auth-checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                />
                I agree to the <a href="/terms" target="_blank">Terms of Service</a> and <a href="/privacy" target="_blank">Privacy Policy</a>
              </label>
            </div>
          )}

          <button
            className="primary-button"
            type="submit"
            disabled={loading}
          >
            {loading ? 'Please wait...' : isSignup ? 'Create Account' : 'Sign In'}
          </button>

          <button
            type="button"
            className="secondary-button"
            onClick={() => {
              setIsSignup(!isSignup);
              setError('');
              setAgeConfirmed(false);
              setTermsAccepted(false);
            }}
            disabled={loading}
          >
            {isSignup ? 'Already have an account? Sign in' : 'Need an account? Sign up'}
          </button>

          <div className="login-modal-divider">
            <div className="divider-line"></div>
            <span className="divider-text">OR</span>
            <div className="divider-line"></div>
          </div>

          <button
            type="button"
            className="primary-button google-button"
            onClick={handleGoogleLogin}
            disabled={loading}
          >
            <span className="google-icon">G</span>
            Continue with Google
          </button>
        </form>
      </div>
    </div>
  );
};

export default LoginModal;
